<script>
	import route from "./stores/route.js";
	import { journeySubscribeThrottled } from "./stores/journey.js";
	import locality from "./stores/locality.js";
	import profile, { profiles } from "./stores/profile.js";
	import chronology from "./stores/chronology.js";
	import windows from "./stores/windows.js";
	import WindowList from "./views/windows/WindowList.svelte";
	import Window from "./views/windows/Window.svelte";
	import Wallpaper from "./views/Wallpaper.svelte";
	import Button from "./views/windows/Button.svelte";
	import isMobile from "./helpers/mobile.js";
	import t from "./localization/t.js";
	import history from "./stores/history.js";
	import { onMount } from "svelte";
	import onboarding from "./stores/onboarding.js";
	import cairns from "./stores/cairn.js";
	import { fetchCairnById, fetchCairnBySlug } from "./api/api.js";
	import { cairnReferenceFromHash } from "./helpers/cairnHash.js";
	import { trackGtagEvent } from "./helpers/gtag.js";
	import "./helpers/monitor.js";
	import { demo, loadDemoRoute } from "./helpers/demo.js";
	import semaphore from "./stores/semaphore.js";

	let demoError = false;
	if (demo) {
		document.body.classList.add("demo");
		if (isMobile) { document.body.classList.add("demo-mobile"); }
	}

	function didToggleDemoDrive() {
		if ($semaphore.isPaused) { semaphore.resume(); }
		else { semaphore.pause("Demo paused"); }
	}

	if (navigator.userAgent.toLowerCase().includes("chrome")) {
		document.body.classList.add("chrome");
	}

	if (navigator.userAgent.toLowerCase().includes("firefox")) {
		document.body.classList.add("firefox");
	}

	// continue if has route and onboarding is complete (normal)
	$: $route && $onboarding.isComplete && chronology.resume();

	$: (() => {
		if ($locality) {
			document.title = `${t("title")} • ${$locality}`;
		}
	})();

	function didPressKey(e) {
		if (e.key === "Escape" && !e.repeat) {
			const window = $windows.find((window) => window.isFocused && !window.isModal);

			if (window) {
				windows.close(window.id);
			}
		}
	}

	function showArrivalWindow() {
		const isAlreadyOpen = !!$windows.find(({ kind, isClosed }) => !isClosed && kind === "arrival");

		if (isAlreadyOpen) {
			return;
		}

		const id = windows.open("arrival");
		windows.focus(id);

		return id;
	}

	function recycleRoute() {
		let isRecycling = false;

		journeySubscribeThrottled(1000, ($journey) => {
			if (isRecycling) {
				return;
			}

			if ($journey.isEnded) {
				isRecycling = true;

				const id = showArrivalWindow();

				if (id !== undefined) {
					trackGtagEvent("trip_arrival_shown", {});
				}

				if ($journey.location) {
					history.add([...$route, $journey.location]);

					route.createRoute($journey.location, $profile).then((isRouteReady) => {
						isRecycling = false;
						windows.close(id);

						if (isRouteReady === true) {
							trackGtagEvent("trip_loop_completed", {});
						} else {
							trackGtagEvent("trip_loop_failed", {});
						}
					});
				} else {
					isRecycling = false;
				}
			}
		});
	}

	function didClickReportBugButton() {
		windows.focus(windows.open("report"));
	}

	function isNumericCairnReference(reference) {
		return /^[0-9]+$/.test(reference);
	}

	async function fetchCairnByReference(reference) {
		if (isNumericCairnReference(reference) === true) {
			const byId = await fetchCairnById(reference);

			if (byId?.cairn !== undefined && byId?.cairn !== null) {
				return byId.cairn;
			}

			const bySlug = await fetchCairnBySlug(reference);
			return bySlug?.cairn;
		}

		const bySlug = await fetchCairnBySlug(reference);

		if (bySlug?.cairn !== undefined && bySlug?.cairn !== null) {
			return bySlug.cairn;
		}

		const byId = await fetchCairnById(reference);
		return byId?.cairn;
	}

	function shouldDisplayOnboarding() {
		return $onboarding.isComplete === false && $chronology.duration === 0;
	}

	function didEnsureOnboardingWindow(shouldFocusExisting = true) {
		if (shouldDisplayOnboarding() === false) {
			return;
		}

		const onboardingWindow = $windows.find(({ kind, isClosed }) => {
			return kind === "onboarding" && isClosed !== true;
		});

		if (onboardingWindow !== undefined) {
			if (shouldFocusExisting === true) {
				windows.focus(onboardingWindow.id);
			}

			return;
		}

		const onboardingWindowId = windows.open("onboarding");

		if (onboardingWindowId === undefined) {
			return;
		}

		windows.focus(onboardingWindowId);
	}

	function isSameCairnReference(current, next) {
		if (current?.id !== undefined && next?.id !== undefined) {
			return current.id === next.id;
		}

		if (current?.slug !== undefined && next?.slug !== undefined) {
			return current.slug === next.slug;
		}

		return false;
	}

	function didOpenCairnWindow(cairn) {
		cairns.remember(cairn);

		const existingCairnWindow = $windows.find(({ kind, isClosed, props }) => {
			return (
				kind === "cairn" &&
				isClosed !== true &&
				isSameCairnReference(props?.cairn, cairn) === true
			);
		});

		if (existingCairnWindow !== undefined) {
			windows.focus(existingCairnWindow.id);
			return true;
		}

		const id = windows.open("cairn", { cairn });

		if (id === undefined) {
			return false;
		}

		windows.focus(id);

		return true;
	}

	async function didResolveCairnHash(hashValue) {
		const reference = cairnReferenceFromHash(hashValue);

		if (reference === undefined || reference.length === 0) {
			return false;
		}

		const referenceKind = isNumericCairnReference(reference) === true ? "numeric" : "slug";

		try {
			const cairn = await fetchCairnByReference(reference);

			if (cairn === undefined || cairn === null) {
				trackGtagEvent("cairn_share_not_found", { reference_kind: referenceKind });
				return false;
			}

			didEnsureOnboardingWindow();
			const opened = didOpenCairnWindow(cairn);

			if (opened === true) {
				trackGtagEvent("cairn_share_opened", { reference_kind: referenceKind });
			}

			return opened;
		} catch (error) {
			console.warn("failed to resolve cairn hash", error);
			trackGtagEvent("cairn_share_error", { reference_kind: referenceKind });
			return false;
		}
	}

	if (!demo) {
		recycleRoute();
	}

	if (isMobile) {
		windows.subscribe((windows) => {
			const height = Math.max(
				Math.max(...windows.filter(({ isClosed }) => !isClosed).map(({ y, height }) => y + height)) + 100,
				window.innerHeight
			);

			document.body.style.height = `${height}px`;
		});
	}

	onMount(() => {
		let isMounted = true;

		async function didChangeHash() {
			if (isMounted === false) {
				return;
			}

			await didResolveCairnHash(location.hash);
		}

		window.addEventListener("hashchange", didChangeHash);

		didChangeHash();

		const startupTimerId = setTimeout(async () => {
			if (demo) {
				try {
					const points = await loadDemoRoute(demo);
					if (!isMounted) { return; }
					profile.set(profiles.car);
					route.set(points);
					onboarding.set({ isComplete: true });
					const map = windows.open("map");
					if (!isMobile) {
						windows.move(map, 20, 90);
						const info = windows.open("info");
						windows.move(info, Math.max(20, window.innerWidth - 295), 90);
					}
					windows.focus(map);
					window.parent.postMessage({ type: "america:demo-ready" }, window.location.origin);
				} catch (error) {
					demoError = true;
					window.parent.postMessage({ type: "america:demo-error" }, window.location.origin);
				}
				return;
			}
			didEnsureOnboardingWindow(false);

			if ($route === undefined) {
				const didStartAtOrigin = await route.createRouteFromQuery($profile);

				if (didStartAtOrigin === false) {
					await route.fetchCannedRoute();
				}
			}
		}, 1_000);

		return () => {
			isMounted = false;
			clearTimeout(startupTimerId);
			window.removeEventListener("hashchange", didChangeHash);
		};
	});
</script>

<svelte:window on:keydown={didPressKey} />

{#if demo}
	<div class="demo-notice">
		{#if demoError}
			<p role="alert">This example could not load. Reload to try again.</p>
		{:else}
			<span>Demo: {demo.name}. Your saved trip stays separate.</span>
			<button on:click={didToggleDemoDrive}>
				{$semaphore.isPaused ? "Resume drive" : "Pause drive"}
			</button>
		{/if}
		<a href="/trip/" target="_top">Open your own trip</a>
	</div>
{/if}

{#if $route && $onboarding.isComplete}
	<WindowList />
{/if}

{#if $route}
	<div class="report">
		<Button action={didClickReportBugButton}>
			<span>Feedback</span>
		</Button>
	</div>
{/if}

{#each $windows as window (window.id)}
	{#if !window.isClosed}
		<Window {window} />
	{/if}
{/each}

<Wallpaper />

<style>
	:global(body.demo .trip-seo-content) { display: none; }
	.demo-notice {
		position: relative;
		z-index: 20001;
		margin: 8px;
		padding: 8px;
		background: #f3ecc9;
		color: #1a1a1a;
		font: 13px/1.5 Arial, sans-serif;
	}
	.demo-notice button, .demo-notice a { margin-left: 8px; }
	@media (min-width: 735px) {
		.demo-notice { margin-left: 140px; }
	}
	@media (max-width: 734px) {
		:global(body.demo-mobile .window) { margin-top: 90px; }
	}
	.report {
		position: fixed;
		bottom: 20px;
		left: 20px;
		z-index: 9091;
		box-shadow: calc(var(--chrome-depth) * 2) calc(var(--chrome-depth) * 2) rgba(0, 0, 0, 0.5);
	}

	@media only screen and (max-width: 734px) {
		.report {
			right: 20px;
			left: unset;
		}
	}

	.report span {
		display: block;
		padding: 2px 0;
	}
</style>
